/**
 * Enhanced Collection Page Functionality
 * Handles scroll position restoration, mobile gallery, and enhanced interactions
 */

class EnhancedCollection {
  constructor() {
    this.init();
  }

  init() {
    this.handleScrollPosition();
    this.initMobileGallery();
    this.initEnhancedInteractions();
    this.initAccessibility();
    this.fixPageBoundaries();
  }

  /**
   * Handle scroll position restoration when returning from product pages
   */
  handleScrollPosition() {
    const STORAGE_KEY = 'collection_scroll_position';
    const STORAGE_URL_KEY = 'collection_url';
    
    // Save scroll position when leaving collection page
    document.addEventListener('DOMContentLoaded', () => {
      // Restore scroll position if coming back to same collection
      const savedUrl = sessionStorage.getItem(STORAGE_URL_KEY);
      const currentUrl = window.location.pathname + window.location.search;
      
      if (savedUrl === currentUrl) {
        const savedPosition = sessionStorage.getItem(STORAGE_KEY);
        if (savedPosition) {
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            window.scrollTo({
              top: parseInt(savedPosition),
              behavior: 'instant'
            });
          });
        }
      }
      
      // Clean up storage for new collection
      sessionStorage.setItem(STORAGE_URL_KEY, currentUrl);
    });

    // Save scroll position before navigating to product
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('.card a, .product-card__link');
      if (productLink && productLink.href && productLink.href.includes('/products/')) {
        const currentUrl = window.location.pathname + window.location.search;
        sessionStorage.setItem(STORAGE_KEY, window.pageYOffset.toString());
        sessionStorage.setItem(STORAGE_URL_KEY, currentUrl);
      }
    });

    // Also handle browser back button
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        const savedPosition = sessionStorage.getItem(STORAGE_KEY);
        const savedUrl = sessionStorage.getItem(STORAGE_URL_KEY);
        const currentUrl = window.location.pathname + window.location.search;
        
        if (savedUrl === currentUrl && savedPosition) {
          window.scrollTo({
            top: parseInt(savedPosition),
            behavior: 'instant'
          });
        }
      }
    });
  }

  /**
   * Initialize mobile-friendly gallery with swipe support
   */
  initMobileGallery() {
    const galleries = document.querySelectorAll('[data-product-gallery]');
    
    galleries.forEach(gallery => {
      this.setupGalleryNavigation(gallery);
      this.setupTouchGestures(gallery);
    });
  }

  setupGalleryNavigation(gallery) {
    const slides = gallery.querySelectorAll('.gallery-cell');
    const dots = gallery.querySelectorAll('.gallery-dot');
    const prevButton = gallery.querySelector('.gallery-prev');
    const nextButton = gallery.querySelector('.gallery-next');
    
    let currentIndex = 0;

    const updateGallery = (index) => {
      // Update slides
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-selected', i === index);
      });
      
      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-selected', i === index);
      });
      
      currentIndex = index;
    };

    // Dot navigation
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => updateGallery(index));
    });

    // Arrow navigation
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
        updateGallery(newIndex);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        const newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
        updateGallery(newIndex);
      });
    }

    // Keyboard navigation
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
        updateGallery(newIndex);
      } else if (e.key === 'ArrowRight') {
        const newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
        updateGallery(newIndex);
      }
    });
  }

  setupTouchGestures(gallery) {
    const slides = gallery.querySelector('.gallery-slides');
    if (!slides) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let isDragging = false;
    let isHorizontalSwipe = false;
    
    const handleStart = (e) => {
      isDragging = true;
      isHorizontalSwipe = false;
      const touch = e.type === 'mousedown' ? e : e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      slides.style.transition = 'none';
    };

    const handleMove = (e) => {
      if (!isDragging) return;
      
      const touch = e.type === 'mousemove' ? e : e.touches[0];
      currentX = touch.clientX;
      currentY = touch.clientY;
      
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);
      
      // Only treat as horizontal swipe if horizontal movement is significantly greater than vertical
      if (deltaX > 20 && deltaX > deltaY * 1.5) {
        isHorizontalSwipe = true;
        e.preventDefault(); // Only prevent default for clear horizontal swipes
        
        // Provide visual feedback during drag
        const translateX = currentX - startX;
        slides.style.transform = `translateX(${translateX}px)`;
      }
    };

    const handleEnd = () => {
      if (!isDragging) return;
      
      isDragging = false;
      slides.style.transition = '';
      slides.style.transform = '';
      
      if (isHorizontalSwipe) {
        const deltaX = currentX - startX;
        const threshold = 50; // Minimum distance for swipe
        
        if (Math.abs(deltaX) > threshold) {
          const direction = deltaX > 0 ? 'right' : 'left';
          const currentIndex = Array.from(slides.children).findIndex(slide => 
            slide.classList.contains('is-selected')
          );
          
          let newIndex;
          if (direction === 'right' && currentIndex > 0) {
            newIndex = currentIndex - 1;
          } else if (direction === 'left' && currentIndex < slides.children.length - 1) {
            newIndex = currentIndex + 1;
          } else {
            return; // No change needed
          }
          
          // Trigger navigation
          const dot = gallery.querySelector(`.gallery-dot:nth-child(${newIndex + 1})`);
          if (dot) dot.click();
        }
      }
      
      isHorizontalSwipe = false;
    };

    // Mouse events for desktop
    slides.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // Touch events for mobile - use passive: true where possible
    slides.addEventListener('touchstart', handleStart, { passive: true });
    slides.addEventListener('touchmove', handleMove, { passive: false });
    slides.addEventListener('touchend', handleEnd, { passive: true });
  }

  /**
   * Initialize enhanced interactions
   */
  initEnhancedInteractions() {
    // Improve loading states for add to cart buttons
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('.quick-add__submit');
      if (addToCartBtn && !addToCartBtn.disabled) {
        this.handleAddToCartLoading(addToCartBtn);
      }
    });

    // Add haptic feedback for supported devices
    this.initHapticFeedback();
  }

  handleAddToCartLoading(button) {
    const originalText = button.innerHTML;
    button.classList.add('loading');
    
    // Auto-remove loading state if no response within 5 seconds
    const timeout = setTimeout(() => {
      button.classList.remove('loading');
    }, 5000);

    // Listen for cart updates to remove loading state
    const cartUpdateHandler = () => {
      clearTimeout(timeout);
      button.classList.remove('loading');
      
      // Show success state briefly
      button.style.background = '#4ade80';
      setTimeout(() => {
        button.style.background = '';
      }, 1000);
      
      document.removeEventListener('cart:updated', cartUpdateHandler);
    };

    document.addEventListener('cart:updated', cartUpdateHandler);
  }

  initHapticFeedback() {
    if ('vibrate' in navigator) {
      // Light haptic feedback for interactions
      document.addEventListener('click', (e) => {
        const interactiveElement = e.target.closest('.quick-add__submit, .gallery-dot, .gallery-prev, .gallery-next');
        if (interactiveElement) {
          navigator.vibrate(10); // Very light vibration
        }
      });
    }
  }

  /**
   * Initialize accessibility improvements
   */
  initAccessibility() {
    // Improve focus management for galleries
    document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
      gallery.setAttribute('role', 'region');
      gallery.setAttribute('aria-label', 'Product image gallery');
      
      const slides = gallery.querySelectorAll('.gallery-cell img');
      slides.forEach((img, index) => {
        img.setAttribute('role', 'img');
        img.setAttribute('aria-label', `Product image ${index + 1} of ${slides.length}`);
      });
    });

    // Improve screen reader announcements for stock levels
    document.querySelectorAll('.stock-level').forEach(stockElement => {
      stockElement.setAttribute('role', 'status');
      stockElement.setAttribute('aria-live', 'polite');
    });

    // Improve price announcements
    document.querySelectorAll('.price--enhanced').forEach(priceElement => {
      priceElement.setAttribute('role', 'text');
      priceElement.setAttribute('aria-label', priceElement.textContent.replace(/\s+/g, ' ').trim());
    });
  }
  
  /**
   * Fix page boundaries and prevent empty white space swiping
   */
  fixPageBoundaries() {
    const pageWidth = document.querySelector('.page-width');
    const productGrid = document.querySelector('.product-grid');
    const collectionGrid = document.querySelector('.collection-product-grid');
    
    // Fix page width container
    if (pageWidth) {
      pageWidth.style.maxWidth = '100vw';
      pageWidth.style.overflowX = 'hidden';
    }
    
    // Fix product grid boundaries
    if (productGrid) {
      productGrid.style.width = '100%';
      productGrid.style.maxWidth = '100%';
      productGrid.style.overflow = 'hidden';
    }
    
    // Fix collection grid
    if (collectionGrid) {
      collectionGrid.style.overflowX = 'hidden';
    }
    
    // Prevent horizontal scrolling on body and html
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    
    // Add viewport meta tag if not present
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(viewport);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new EnhancedCollection());
} else {
  new EnhancedCollection();
}

// Also handle page transitions in SPA-style navigation
document.addEventListener('shopify:section:load', () => new EnhancedCollection());

// ====== TARGETED AJAX FILTER FIX (hooks into Shopify's system) ======

// Monitor product grid for AJAX changes and reinitialize components
function setupAjaxFilterFix() {
  const productGrid = document.getElementById('ProductGridContainer');
  if (!productGrid) {
    console.warn('ProductGridContainer not found - AJAX filter fix not initialized');
    return;
  }

  console.log('🎯 Setting up AJAX filter fix...');

  // Watch for changes to the product grid (AJAX filter updates)
  const observer = new MutationObserver((mutations) => {
    let hasProductGridChanges = false;
    
    mutations.forEach((mutation) => {
      // Detect when new product cards are added
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && // Element node
              (node.matches?.('.grid__item') || 
               node.querySelector?.('.grid__item') ||
               node.matches?.('#product-grid') ||
               node.querySelector?.('#product-grid'))) {
            hasProductGridChanges = true;
          }
        });
      }
    });

    if (hasProductGridChanges) {
      console.log('🔄 AJAX filter detected - reinitializing components...');
      console.log('📊 Current product grid state:', {
        totalItems: document.querySelectorAll('#ProductGridContainer .grid__item').length,
        galleries: document.querySelectorAll('#ProductGridContainer [data-product-gallery]').length,
        images: document.querySelectorAll('#ProductGridContainer img[data-src]').length,
        quickviewButtons: document.querySelectorAll('#ProductGridContainer .quick-view-button').length
      });
      
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        reinitializeLazyLoading();
        reinitializeProductGalleries();
        reinitializeQuickView();
        
        // Debug: final state after reinitialization
        console.log('✅ Reinitialization complete - final state:', {
          initializedGalleries: document.querySelectorAll('#ProductGridContainer [data-gallery-initialized]').length,
          initializedImages: document.querySelectorAll('#ProductGridContainer [data-lazy-initialized]').length,
          initializedQuickview: document.querySelectorAll('#ProductGridContainer [data-quickview-initialized]').length
        });
      }, 100);
    }
  });

  // Observe the product grid container
  observer.observe(productGrid, {
    childList: true,
    subtree: true
  });

  console.log('✅ AJAX filter fix initialized');
}

// Reinitialize lazy loading for new images
function reinitializeLazyLoading() {
  console.log('🖼️ Reinitializing lazy loading...');
  
  // Use the existing lazy loading system if available
  if (typeof initLazyLoading === 'function') {
    try {
      initLazyLoading();
      console.log('✅ Lazy loading reinitialized using existing function');
      return;
    } catch (error) {
      console.warn('Error calling initLazyLoading:', error);
    }
  }
  
  // Fallback: manually set up lazy loading for new images
  const newImages = document.querySelectorAll('#ProductGridContainer img[data-src]:not([data-lazy-initialized])');
  
  if (newImages.length > 0) {
    console.log(`📸 Found ${newImages.length} new images to initialize`);
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const image = entry.target;
            const src = image.getAttribute('data-src');
            const srcset = image.getAttribute('data-srcset');
            
            if (src) {
              image.src = src;
              image.removeAttribute('data-src');
            }
            if (srcset) {
              image.srcset = srcset;
              image.removeAttribute('data-srcset');
            }
            
            imageObserver.unobserve(image);
          }
        });
      });
      
      newImages.forEach((image) => {
        image.setAttribute('data-lazy-initialized', 'true');
        imageObserver.observe(image);
      });
    } else {
      // Fallback for browsers without IntersectionObserver
      newImages.forEach((image) => {
        const src = image.getAttribute('data-src');
        const srcset = image.getAttribute('data-srcset');
        
        if (src) {
          image.src = src;
          image.removeAttribute('data-src');
        }
        if (srcset) {
          image.srcset = srcset;
          image.removeAttribute('data-srcset');
        }
        
        image.setAttribute('data-lazy-initialized', 'true');
      });
    }
    
    console.log('✅ New images initialized for lazy loading');
  }
}

// Reinitialize product galleries for new product cards
function reinitializeProductGalleries() {
  console.log('🖼️ Reinitializing product galleries...');
  
  // Find new product galleries that haven't been initialized
  const newGalleries = document.querySelectorAll('#ProductGridContainer [data-product-gallery]:not([data-gallery-initialized])');
  
  if (newGalleries.length > 0) {
    console.log(`🎠 Found ${newGalleries.length} new product galleries to initialize`);
    
    newGalleries.forEach((gallery) => {
      // Mark as initialized to prevent duplicates
      gallery.setAttribute('data-gallery-initialized', 'true');
      
      // Create new ProductGallery instance for this gallery
      try {
        // Check if ProductGallery class is available
        if (typeof ProductGallery !== 'undefined') {
          const instance = new ProductGallery(gallery);
          console.log('✅ ProductGallery instance created for gallery:', gallery);
          
          // Debug: check if arrows exist
          const prevBtn = gallery.querySelector('.gallery-prev');
          const nextBtn = gallery.querySelector('.gallery-next');
          console.log('🔍 Gallery arrows found:', { prev: !!prevBtn, next: !!nextBtn });
          
        } else {
          console.warn('⚠️ ProductGallery class not found');
        }
      } catch (error) {
        console.warn('Error creating ProductGallery instance:', error);
      }
    });
    
    console.log('✅ Product galleries reinitialized');
  } else {
    console.log('ℹ️ No new product galleries found');
  }
}

// Reinitialize quickview for new buttons
function reinitializeQuickView() {
  console.log('⚡ Reinitializing quickview...');
  
  // Use the existing quickview initialization if available
  if (typeof initQuickView === 'function') {
    try {
      initQuickView();
      console.log('✅ Quickview reinitialized using existing function');
      return;
    } catch (error) {
      console.warn('Error calling initQuickView:', error);
    }
  }
  
  // Alternative: try to reinitialize the entire product card gallery system
  if (typeof initProductCardGallery === 'function') {
    try {
      initProductCardGallery();
      console.log('✅ Product card gallery reinitialized (includes quickview)');
      return;
    } catch (error) {
      console.warn('Error calling initProductCardGallery:', error);
    }
  }
  
  // Fallback: check if we have new buttons and trigger a warning
  const newQuickViewButtons = document.querySelectorAll('#ProductGridContainer .quick-view-button:not([data-quickview-initialized])');
  
  if (newQuickViewButtons.length > 0) {
    console.log(`⚡ Found ${newQuickViewButtons.length} new quickview buttons`);
    console.log('⚠️ Neither initQuickView nor initProductCardGallery functions are available');
    console.log('📝 Manual quickview initialization may be needed');
    
    // Mark them to prevent repeated warnings
    newQuickViewButtons.forEach((button) => {
      button.setAttribute('data-quickview-initialized', 'true');
    });
  }
}

// Mark existing galleries as initialized to prevent conflicts
function markExistingGalleriesAsInitialized() {
  const existingGalleries = document.querySelectorAll('[data-product-gallery]:not([data-gallery-initialized])');
  existingGalleries.forEach(gallery => {
    gallery.setAttribute('data-gallery-initialized', 'true');
  });
  
  const existingImages = document.querySelectorAll('img[data-src]:not([data-lazy-initialized])');
  existingImages.forEach(image => {
    image.setAttribute('data-lazy-initialized', 'true');
  });
  
  const existingQuickviewButtons = document.querySelectorAll('.quick-view-button:not([data-quickview-initialized])');
  existingQuickviewButtons.forEach(button => {
    button.setAttribute('data-quickview-initialized', 'true');
  });
}

// Initialize filter positioning fix
function setupFilterPositioning() {
  if (window.innerWidth < 750) return; // Desktop only

  console.log('🎯 Setting up filter positioning...');

  // Watch for filter dropdown openings
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('.facets__summary');
    if (!summary) return;

    const disclosure = summary.closest('.facets__disclosure');
    const display = disclosure?.querySelector('.facets__display');
    
    if (!disclosure || !display) return;

    // Small delay to let the dropdown become visible
    setTimeout(() => {
      if (disclosure.hasAttribute('open') && 
          window.getComputedStyle(display).display !== 'none') {
        positionFilterDropdown(summary, display);
      }
    }, 10);
  });

  // Handle window resize - reposition any open dropdowns
  window.addEventListener('resize', () => {
    if (window.innerWidth >= 750) {
      const openDropdowns = document.querySelectorAll('.facets__disclosure[open] .facets__display');
      openDropdowns.forEach(dropdown => {
        const disclosure = dropdown.closest('.facets__disclosure');
        const summary = disclosure?.querySelector('.facets__summary');
        if (summary && window.getComputedStyle(dropdown).display !== 'none') {
          positionFilterDropdown(summary, dropdown);
        }
      });
    }
  });

  console.log('✅ Filter positioning initialized');
}

// Position a filter dropdown safely within viewport
function positionFilterDropdown(trigger, dropdown) {
  const triggerRect = trigger.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Calculate safe position
  let left = triggerRect.left;
  let top = triggerRect.bottom + 8;
  
  // Get dropdown dimensions
  const dropdownWidth = Math.min(480, viewportWidth * 0.8); // 30rem max or 80% viewport
  const dropdownHeight = Math.min(400, viewportHeight * 0.5); // 50% max viewport height
  const margin = 16; // 1rem margin
  
  // Adjust horizontal position to stay within viewport
  if (left + dropdownWidth > viewportWidth - margin) {
    left = viewportWidth - dropdownWidth - margin;
  }
  if (left < margin) {
    left = margin;
  }
  
  // Adjust vertical position if would overflow bottom
  if (top + dropdownHeight > viewportHeight - margin) {
    const topPosition = triggerRect.top - dropdownHeight - 8;
    if (topPosition > margin) {
      top = topPosition;
    } else {
      top = margin;
    }
  }
  
  // Apply positioning
  dropdown.style.setProperty('left', `${left}px`, 'important');
  dropdown.style.setProperty('top', `${top}px`, 'important');
  
  console.log(`📍 Filter positioned at: left=${left}px, top=${top}px`);
}

// Initialize both systems when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    markExistingGalleriesAsInitialized();
    setupAjaxFilterFix();
    setupFilterPositioning();
  });
} else {
  markExistingGalleriesAsInitialized();
  setupAjaxFilterFix();
  setupFilterPositioning();
}